import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import { RefObject, useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { FormTemplate } from "../../db/schema";
import { fillFormsFromCase } from "../../services/formAudioProcessingService";
import { resolveFormPdfUrl } from "../../services/pdfService";
import Button from "../common/Button";
import ListDetailModal from "../common/ListDetailModal";
import PdfFileViewer from "../Investigations/viewers/PdfFileViewer";
import TemplatesPickerList, { templateUnusableReason } from "./TemplatesPickerList";

// "Export to a form", from inside a case: pick one or more of your forms (tick
// them, or click one to see it first) and fill them out from this case's
// narrative and recordings. The mirror of Fill Form > From a Case on the Forms
// page. A case often needs several forms, so any number can be chosen.

// The form itself, read-only, with the action to fill it.
const TemplatePreview: React.FC<{ template: FormTemplate; onBack: () => void; onFill: () => void }> = ({ template, onBack, onFill }) => {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    resolveFormPdfUrl(template.original_pdf_url).then((u) => !cancelled && setUrl(u || null));
    return () => {
      cancelled = true;
    };
  }, [template.original_pdf_url]);
  const reason = templateUnusableReason(template);

  return (
    <div className="flex-1 flex flex-col min-w-0 min-h-0">
      <div className="px-4 py-3 border-b border-base-300 flex items-center gap-3 flex-wrap">
        <Button variant="secondary" size="sm" onClick={onBack}>
          <ArrowLeftIcon className="h-4 w-4 mr-1" /> Back
        </Button>
        <div className="min-w-0 flex-1">
          <h1 className="font-semibold text-base-content truncate m-0 text-base">{template.name}</h1>
          <p className="text-xs text-base-content/60 m-0">
            {template.fields?.length || 0} fields · {template.scope === "agency" ? "Department form" : "Personal form"}
          </p>
        </div>
        <Button variant="primary" size="sm" disabled={!!reason} title={reason} onClick={onFill}>
          Fill out this form
        </Button>
      </div>
      <div className="flex-1 overflow-auto p-6">
        <div className="h-[65vh] rounded-xl border border-base-300 bg-base-100 overflow-hidden">
          {url ? <PdfFileViewer url={url} /> : <p className="p-6 text-base-content/60">Loading the form...</p>}
        </div>
      </div>
    </div>
  );
};

const FormExportPickerModal: React.FC<{ modalRef: RefObject<HTMLDialogElement>; caseId: string }> = ({ modalRef, caseId }) => {
  const navigate = useNavigate();

  // The ticked forms. Kept here (not in the list) so Back from looking at a form
  // can decide what to do with them. A fresh dialog always starts with none.
  const [selected, setSelected] = useState<Map<string, FormTemplate>>(new Map());
  useEffect(() => {
    const dialog = modalRef.current;
    if (!dialog) return;
    const reset = () => setSelected(new Map());
    dialog.addEventListener("close", reset);
    return () => dialog.removeEventListener("close", reset);
  }, [modalRef]);

  // One submission per form, all read from this case. When they have all
  // finished, one toast says how it went.
  const fill = (templates: FormTemplate[]) => {
    if (!templates.length) return;
    modalRef.current?.close();
    const names = new Map(templates.map((t) => [t.id, t.name]));
    void fillFormsFromCase(
      caseId,
      templates.map((t) => t.id),
      {
        // Clicking the "filling..." toast goes to the forms, where each new
        // submission is listed (under Submissions) as it is read.
        onViewProgress: () => navigate("/forms"),
        onFinished: (results) => {
          const filled = results.filter((r) => r.submissionId && !r.error);
          const failed = results.filter((r) => r.error);

          if (failed.length) {
            const reasons = Array.from(new Set(failed.map((r) => r.error)));
            toast.error(
              failed.length === results.length && reasons.length === 1
                ? reasons[0]!
                : `${failed.length} of ${results.length} forms could not be filled: ${failed
                    .map((r) => `${names.get(r.templateId)} (${r.error})`)
                    .join("; ")}`,
              { duration: 10000 },
            );
          }
          if (!filled.length) return;

          // Stay in the case: offer to open what was made instead of pulling the officer away.
          const single = filled.length === 1;
          toast(
            (t) => (
              <span className="flex items-center gap-3">
                {single ? `"${names.get(filled[0].templateId)}" is filled out.` : `${filled.length} forms are filled out.`}
                <Button
                  variant="primary"
                  size="xs"
                  onClick={() => {
                    toast.dismiss(t.id);
                    navigate(single ? `/forms/${filled[0].templateId}/submissions/${filled[0].submissionId}` : "/forms");
                  }}
                >
                  {single ? "Open form" : "View forms"}
                </Button>
              </span>
            ),
            { duration: 20000 },
          );
        },
      },
    );
  };

  return (
    <ListDetailModal<FormTemplate>
      modalRef={modalRef}
      title="Fill forms from this case"
      renderList={(open) => (
        <TemplatesPickerList
          picker={{
            confirmLabel: (n) => (n === 1 ? "Fill out 1 form" : `Fill out ${n} forms`),
            onConfirm: fill,
            onOpenTemplate: open,
            selected,
            onSelectedChange: setSelected,
          }}
        />
      )}
      renderDetail={(template, back) => (
        <TemplatePreview
          template={template}
          // Looking at one of the forms you ticked keeps your ticks. Looking at a
          // form you did NOT tick and coming Back is a change of mind: clear them all.
          onBack={() => {
            if (!selected.has(template.id)) setSelected(new Map());
            back();
          }}
          onFill={() => fill([template])}
        />
      )}
    />
  );
};

export default FormExportPickerModal;
